import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { backfillRjIdFromId } from "@/api/collapsedId";
import { withJWTClient } from "@/api/connectClient";
import { CONNECT_API_V1_BASE_URL, updateMaskParam } from "@/api/gateway";
import { accountKeys } from "@/api/siteDetails";

const API_BASE_URL = CONNECT_API_V1_BASE_URL;

export const greenarrowEmailConfigSchema = z.object({
  bounce_domain: z.string().nullish(),
  tracking_domain: z.string().nullish(),
  sender_domain_status: z.string().nullish(),
  bounce_domain_status: z.string().nullish(),
  tracking_domain_status: z.string().nullish(),
  // protojson serializes the Int64Value as a JSON string; accept both.
  mail_class_id: z.union([z.number(), z.string()]).nullish(),
  mail_class_name: z.string().nullish(),
  virtual_mta_ip: z.string().nullish(),
});

const emailProviderConfigSchema = z
  .object({ greenarrow: greenarrowEmailConfigSchema.nullish() })
  .nullish();

export const infobipTextConfigSchema = z.object({
  base_url: z.string().nullish(),
  api_key_prefix: z.string().nullish(),
  sender_id: z.string().nullish(),
  entity_id: z.string().nullish(),
  extra: z.record(z.string(), z.unknown()).nullish(),
  rcs_sender_name: z.string().nullish(),
  brand_registration_id: z.string().nullish(),
});

const textProviderConfigSchema = z
  .object({ infobip: infobipTextConfigSchema.nullish() })
  .nullish();

const structSchema = z.record(z.string(), z.unknown()).nullable();

// Read-only account-standing projection carried on the site payload (connect-api
// #247). `is_active` is the derived "account in good standing" flag; `status` is
// the raw billing status (e.g. "active", "cancelled"). Optional so the FE stays
// resilient whether or not #247 has shipped to the target environment yet.
export const siteAccountSchema = z.object({
  status: z.string(),
  is_active: z.boolean(),
  name: z.string(),
  status_change_date: z.string().nullish(),
});

export type SiteAccount = z.infer<typeof siteAccountSchema>;

export const utmCustomParamSchema = z
  .object({
    key: z.string().nullish(),
    value: z.string().nullish(),
  })
  .passthrough();

export type UtmCustomParam = z.infer<typeof utmCustomParamSchema>;

export const utmSubsectionSchema = z
  .object({
    append_disabled: z.boolean().nullish(),
    campaign: z.string().nullish(),
    content: z.string().nullish(),
    medium: z.string().nullish(),
    custom_params: z.array(utmCustomParamSchema).nullish(),
  })
  .passthrough();

export type UtmChannelConfig = z.infer<typeof utmSubsectionSchema>;

export const utmConfigSchema = z
  .object({
    source: z.string().nullish(),
    email: utmSubsectionSchema.nullish(),
    text: utmSubsectionSchema.nullish(),
  })
  .passthrough();

export type UtmConfig = z.infer<typeof utmConfigSchema>;

export const consentKeywordSchema = z.object({
  keyword: z.string().nullish(),
  action: z.string().nullish(),
});

export type ConsentKeyword = z.infer<typeof consentKeywordSchema>;

export const textConsentConfigSchema = z.object({
  keywords: z.array(consentKeywordSchema).nullish(),
  tcpa_disclosure: z.string().nullish(),
});

export type TextConsentConfig = z.infer<typeof textConsentConfigSchema>;

export const tcrRegistrationSchema = z.object({
  company_name: z.string().nullish(),
  website_url: z.string().nullish(),
  business_email: z.string().nullish(),
  business_phone: z.string().nullish(),
  country: z.string().nullish(),
  business_address: z.string().nullish(),
  brand_status: z.string().nullish(),
  campaign_status: z.string().nullish(),
});

export type TcrRegistration = z.infer<typeof tcrRegistrationSchema>;

export const siteSchema = z.preprocess(
  backfillRjIdFromId,
  z.object({
    id: z.union([z.number(), z.string()]),
    rj_id: z.string(),
    account_id: z.number(),
    utm_config: utmConfigSchema.nullish(),
    url: z.string(),
    timezone: z.string(),
    platform: z.string(),
    currency: z.string(),
    show_branding: z.boolean().nullable(),
    tracking_links_enabled: z.boolean().nullable(),
    google_analytics_id: z.string(),
    google_analytics_is_active: z.boolean().nullable(),
    suppressed_domains: z.array(z.string()),
    default_from_name: z.string(),
    default_from_email: z.string(),
    default_reply_to: z.string(),
    bcc_emails_enabled: z.boolean().nullable(),
    bcc_email_address: z.string(),
    recommendations_days_back: z.number(),
    recommendations_ignored_products: z.array(z.string()),
    recommendations_ignored_skus: z.array(z.string()),
    recommendations_ignored_categories: z.array(z.string()),
    recommendations_ignored_brands: z.array(z.string()),
    gdpr_enabled: z.boolean().nullable(),
    restrict_email_eligibility_to_opt_in: z.boolean().nullish(),
    gdpr_request_email: z.string(),
    gdpr_results_email: z.string(),
    frequency_cap_day_count: z.number(),
    frequency_cap_week_count: z.number(),
    frequency_cap_month_count: z.number(),
    preheader_styles: z.string(),
    sender_domain: z.string(),
    two_step_unsubscribe: z.boolean().nullable(),
    broadcast_warmup_notification_enabled: z.boolean().nullish(),
    sms_enabled: z.boolean().nullable(),
    brand_name: z.string(),
    sms_primary_phone_number: z.string(),
    is_connect: z.boolean().nullable(),
    is_internal: z.boolean().nullish(),
    test_mode: z.boolean().nullable(),
    model_distribution_config: structSchema,
    csv_integration_data: structSchema,
    create_date: z.string().nullish(),
    modify_date: z.string().nullish(),
    deleted_date: z.string().nullish(),
    onboarding_metadata: structSchema,
    email_provider_config: emailProviderConfigSchema,
    email_validation_enabled: z.boolean().nullable(),
    frames_enabled: z.boolean().nullable(),
    frames_config: structSchema,
    is_skustore_custom_field_mapping_enabled: z.boolean().nullish(),
    modified_by: z.string(),
    monitoring_enabled: z.boolean().nullable(),
    is_analog_sequence_enabled: z.boolean().nullable(),
    text_provider: z.string(),
    text_provider_config: textProviderConfigSchema,
    tags: z.array(z.string()),
    myshopify_url: z.string(),
    email_provider: z.string(),
    append_footer_to_emails: z.boolean().nullable(),
    disallowed_zb_status_codes: z.array(z.string()),
    sms_quiet_hour_start: z.number().nullable(),
    sms_quiet_hour_end: z.number().nullable(),
    sms_send_window_start: z.number().nullish(),
    sms_send_window_end: z.number().nullish(),
    text_consent_config: textConsentConfigSchema.nullish(),
    tcr_registration: tcrRegistrationSchema.nullish(),
    privacy_policy_url: z.string().nullish(),
    terms_of_service_url: z.string().nullish(),
    account: siteAccountSchema.nullish(),
  }),
);

export type Site = z.infer<typeof siteSchema>;

export const siteListingSchema = z.preprocess(
  backfillRjIdFromId,
  z.object({
    id: z.union([z.number(), z.string()]),
    rj_id: z.string(),
    account_id: z.number(),
    url: z.string(),
    brand_name: z.string(),
    is_connect: z.boolean(),
    test_mode: z.boolean(),
    is_analog_sequence_enabled: z.boolean(),
    text_provider: z.string(),
    sms_primary_phone_number: z.string(),
    deleted_at: z.string().nullish(),
    account: siteAccountSchema.nullish(),
  }),
);

export type SiteListing = z.infer<typeof siteListingSchema>;

const retrieveAllSitesResponseSchema = z.object({
  sites: z.array(siteListingSchema),
});

export type SitePatch = Record<string, unknown>;

export const zeroSiteKeys = {
  all: () => ["zero", "sites"] as const,
  list: (params: RetrieveAllSitesParams) =>
    ["zero", "sites", "list", params] as const,
  detail: (rjId: string) => ["zero", "sites", "detail", rjId] as const,
};

// connect-api #247 made GET /v1/sites default to active-account sites only.
// account_state selects which sites the list returns: "active" | "inactive" |
// "all". These ZeroAdmin/admin flows list every site across accounts, so we
// default to "all" to preserve pre-#247 behavior; callers can narrow it.
export type AccountState = "active" | "inactive" | "all";

export interface RetrieveAllSitesParams {
  isConnectOnly?: boolean;
  includeDeleted?: boolean;
  search?: string;
  limit?: number;
  accountState?: AccountState;
}

// Page size for the async site picker (typeahead). The user narrows with the
// search box rather than scrolling; results beyond this are not loaded.
export const SITE_PICKER_LIMIT = 25;

export async function getSites(
  params: RetrieveAllSitesParams = {},
): Promise<SiteListing[]> {
  const query = new URLSearchParams();
  if (params.isConnectOnly) query.set("is_connect_only", "true");
  if (params.includeDeleted) query.set("include_deleted", "true");
  if (params.search) query.set("search", params.search);
  if (params.limit != null) query.set("limit", String(params.limit));
  query.set("account_state", params.accountState ?? "all");
  const qs = query.toString();

  const response = await withJWTClient((client) =>
    client.get(`${API_BASE_URL}/sites${qs ? `?${qs}` : ""}`),
  );
  return retrieveAllSitesResponseSchema.parse(response.data).sites;
}

export async function getSite(rjId: string): Promise<Site> {
  // connect-api #247 made GET /v1/sites/{id} return 400 (FailedPrecondition)
  // for inactive-account sites unless accept_inactive=true. ZeroAdmin must load
  // any site regardless of account standing, so we always opt in here.
  const query = new URLSearchParams({ accept_inactive: "true" });
  const response = await withJWTClient((client) =>
    client.get(`${API_BASE_URL}/sites/${rjId}?${query.toString()}`),
  );
  return siteSchema.parse(response.data);
}

export async function updateSite(
  rjId: string,
  site: SitePatch,
  updateMask: string[],
): Promise<Site> {
  const query = updateMaskParam(updateMask);
  const response = await withJWTClient((client) =>
    client.patch(`${API_BASE_URL}/sites/${rjId}?${query.toString()}`, site),
  );
  return siteSchema.parse(response.data);
}

export function useSites(params: RetrieveAllSitesParams = {}) {
  return useQuery({
    queryKey: zeroSiteKeys.list(params),
    queryFn: () => getSites(params),
  });
}

// useSitesSearch backs an async typeahead picker: server-side substring search
// over brand_name/url, capped at SITE_PICKER_LIMIT results.
export function useSitesSearch(
  search: string,
  params: Omit<RetrieveAllSitesParams, "search" | "limit"> = {},
) {
  const queryParams: RetrieveAllSitesParams = {
    ...params,
    search,
    limit: SITE_PICKER_LIMIT,
  };
  return useQuery({
    queryKey: zeroSiteKeys.list(queryParams),
    queryFn: () => getSites(queryParams),
  });
}

export function useSite(rjId: string) {
  return useQuery({
    queryKey: zeroSiteKeys.detail(rjId),
    queryFn: () => getSite(rjId),
    enabled: !!rjId,
  });
}

export function useUpdateSite(rjId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["updateSite", rjId],
    mutationFn: ({
      site,
      updateMask,
    }: {
      site: SitePatch;
      updateMask: string[];
    }) => updateSite(rjId, site, updateMask),
    onSuccess: (updated) => {
      queryClient.setQueryData(zeroSiteKeys.detail(rjId), updated);
      void queryClient.invalidateQueries({ queryKey: zeroSiteKeys.all() });
      // Same site, different cache — keeps the app shell's banners in sync.
      void queryClient.invalidateQueries({ queryKey: accountKeys.site(rjId) });
    },
  });
}
